import { useEffect } from 'react';
import { uniq, path } from 'ramda';
import {
  useFetchAlerts,
  useCreateAlert,
  useRemoveAlert
} from '../redux/hooks';

export const useAlertSetSync = ({
  alertSets,
  groupMembers,
  userId
}) => {
  const { alerts, fetchAlerts, fetchAlertsPending } = useFetchAlerts();
  const { createAlert, createAlertsInProgress, createAlertsFailed } = useCreateAlert();
  const { removeAlert, removeAlertsInProgress, removeAlertsFailed } = useRemoveAlert();

  // Fetch alerts when component mounts
  useEffect(() => {
    if (userId && !alerts) {
      fetchAlerts({
        userId
      });
    }
  }, [userId, fetchAlerts, alerts]);

  // Sync alerts
  useEffect(() => {
    // there should always be at least one group member
    if (!alerts || !alertSets || !groupMembers || groupMembers.length < 1 || fetchAlertsPending) {
      return;
    }

    // Get all alert set IDs that are assigned to group members
    const assignedAlertSetIds = uniq(groupMembers.reduce((acc, m) => {
      const memberAlertSets = path(['alertSets'], m) || [];
      return acc.concat(memberAlertSets.map(ap => ap._id));
    }, []));


    // Get all alert sets that are assigned
    const assignedAlertSets = alertSets.filter(as => assignedAlertSetIds.includes(as._id));

    // Get all alerts from assigned alert sets
    const allAssignAlerts = assignedAlertSets.reduce((acc, as) => {
      return acc.concat(as.alerts.map(a => ({
        ...a,
        alertSetId: as._id
      })));
    }, []);


    // Find alerts that need to be created
    const alertsToCreate = allAssignAlerts
      .filter(a => !alerts.find(aa => aa.alertSetId === a.alertSetId && aa.id === a.id))
      .filter(a => !createAlertsInProgress.includes(a.id))
      .filter(a => !createAlertsFailed.includes(a.id));

    if (alertsToCreate.length > 0) {
      console.log('creating alerts', alertsToCreate);
      createAlert(alertsToCreate);
    }

    // Find alerts that need to be removed
    const alertsToRemove = alerts
      .filter(a => a.alertSetId && !allAssignAlerts.find(aa => aa.alertSetId === a.alertSetId && aa.id === a.id))
      .filter(a => !removeAlertsInProgress.includes(a._id))
      .filter(a => !removeAlertsFailed.includes(a._id));

    if (alertsToRemove.length > 0) {
      console.log('removing alerts', alertsToRemove);
      removeAlert(alertsToRemove);
    }
  }, [
    alerts,
    alertSets,
    groupMembers,
    fetchAlertsPending,
    createAlertsInProgress,
    createAlertsFailed,
    createAlert,
    removeAlert,
    removeAlertsFailed,
    removeAlertsInProgress
  ]);

  // Return alerts in case parent needs access to them
  return {
    alerts,
    fetchAlertsPending
  };
};